import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  BookOpen,
  FolderOpen,
  Scale,
  FileText,
  Settings,
  HelpCircle,
  ChevronLeft,
  Trash2,
  LogOut,
} from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { useAuth } from '../lib/AuthContext'

interface NavItem {
  key: string
  icon: React.ComponentType<{ className?: string }>
  to: string
}

const NAV_ITEMS_TOP: NavItem[] = [
  { key: 'dashboard', icon: LayoutDashboard, to: '/' },
  { key: 'knowledgeBase', icon: BookOpen, to: '/knowledge' },
  { key: 'caseList', icon: FolderOpen, to: '/matters' },
  { key: 'legalFoundation', icon: Scale, to: '/legal-base' },
]

const NAV_ITEMS_BOTTOM: NavItem[] = [
  { key: 'docTemplates', icon: FileText, to: '/templates' },
  { key: 'recycleBin', icon: Trash2, to: '/recycle-bin' },
  { key: 'settings', icon: Settings, to: '/settings' },
  { key: 'helpCenter', icon: HelpCircle, to: '/help' },
]

const NAV_LABELS: Record<string, Record<string, string>> = {
  zh: {
    dashboard: '工作台',
    knowledgeBase: '分类知识库',
    caseList: '案件列表',
    legalFoundation: '社区法律基金会',
    docTemplates: '文书模板',
    recycleBin: '回收站',
    settings: '设置',
    helpCenter: '帮助中心',
    logout: '退出登录',
  },
  en: {
    dashboard: 'Dashboard',
    knowledgeBase: 'Knowledge Base',
    caseList: 'Cases',
    legalFoundation: 'Community Law Foundation',
    docTemplates: 'Templates',
    recycleBin: 'Recycle Bin',
    settings: 'Settings',
    helpCenter: 'Help Center',
    logout: 'Sign Out',
  },
}

interface Props {
  collapsed?: boolean
  onToggleCollapse?: () => void
}

export function AppSidebar({ collapsed, onToggleCollapse }: Props) {
  const { lang } = useI18n()
  const labels = NAV_LABELS[lang] ?? NAV_LABELS.en
  const { logout } = useAuth()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <aside
      className={`flex flex-col h-screen bg-navy-950 text-white transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-56'
      }`}
    >
      {/* Logo area */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10">
        <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center flex-shrink-0">
          <Scale className="w-5 h-5 text-white" />
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">AI Lawyer</div>
            <div className="text-[10px] text-white/50">AI Lawyer MVP</div>
          </div>
        )}
        <button
          onClick={onToggleCollapse}
          className="p-1 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors flex-shrink-0"
          aria-label="Toggle sidebar"
        >
          <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Top navigation */}
      <nav className="flex-1 py-3 px-2 space-y-1">
        {NAV_ITEMS_TOP.map((item) => (
          <SidebarLink
            key={item.key}
            item={item}
            label={labels[item.key]}
            collapsed={collapsed}
          />
        ))}
      </nav>

      {/* Bottom navigation */}
      <nav className="py-3 px-2 space-y-1 border-t border-white/10">
        {NAV_ITEMS_BOTTOM.map((item) => (
          <SidebarLink
            key={item.key}
            item={item}
            label={labels[item.key]}
            collapsed={collapsed}
          />
        ))}
      </nav>

      {/* Logout */}
      <div className="px-2 pb-1">
        <button
          onClick={handleLogout}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors w-full text-white/50 hover:bg-red-600/20 hover:text-red-300 ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? labels.logout : undefined}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="truncate">{labels.logout}</span>}
        </button>
      </div>

      {/* Footer */}
      {!collapsed && (
        <div className="px-4 py-3 text-[10px] text-white/30">
          AI Lawyer MVP
          <br />
          Version 1.0.6
        </div>
      )}
    </aside>
  )
}

function SidebarLink({
  item,
  label,
  collapsed,
}: {
  item: NavItem
  label: string
  collapsed?: boolean
}) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
          isActive
            ? 'bg-blue-600 text-white font-medium'
            : 'text-white/70 hover:bg-white/10 hover:text-white'
        } ${collapsed ? 'justify-center' : ''}`
      }
      title={collapsed ? label : undefined}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  )
}
